import { http } from '@config/api';

export async function initiateUpload(fileName: string) {
	console.log(`Initiating upload for file: ${fileName}`);
	
	try {
		const response = await http.post<{
			success: boolean;
			content: { uploadId: string; key: string };
			error: string;
		}>('file-manager', 'uploads/initiate', {
			filename: fileName,
		});
		
		console.log(`Upload initiated successfully for ${fileName}:`, {
			uploadId: response.data.content.uploadId,
			key: response.data.content.key,
			success: response.data.success
		});
		
		return response.data;
	} catch (error: any) {
		console.error(`Failed to initiate upload for ${fileName}:`, {
			error: error.message,
			status: error.response?.status,
			data: error.response?.data
		});
		throw error;
	}
}

export async function getPartUrl(
	fileName: string,
	key: string,
	uploadId: string,
	partNumber: number
) {
	console.log(`Getting part URL for part ${partNumber}:`, {
		fileName,
		key,
		uploadId,
		partNumber
	});
	
	try {
		const response = await http.post<{
			content: {
				url: string;
				partNumber: number;
				uploadId: string;
			};
		}>('file-manager', 'uploads/part-url', {
			fileName,
			key,
			uploadId,
			partNumber,
		});
		const res = response.data;

		console.log(`Part URL retrieved successfully for part ${partNumber}:`, {
			hasUrl: !!res.content?.url,
			returnedUploadId: res.content?.uploadId,
			requestedUploadId: uploadId,
			uploadIdMatch: res.content?.uploadId === uploadId,
			partNumber
		});

		return res.content;
	} catch (error: any) {
		console.error(`Failed to get part URL for part ${partNumber}:`, {
			error: error.message,
			status: error.response?.status,
			data: error.response?.data,
			fileName,
			key,
			uploadId,
			partNumber
		});
		throw error;
	}
}

export async function completeUpload(key: string, uploadId: string, parts: any[]) {
	const MAX_RETRIES = 3;
	const RETRY_DELAY = 2000; // 2 seconds
	
	// Validate input parameters
	if (!key || !uploadId || !parts || parts.length === 0) {
		throw new Error('Invalid parameters for completeUpload: key, uploadId, and parts are required');
	}

	// Validate parts array structure
	const invalidParts = parts.filter(part => !part.partNumber || !part.eTag);
	if (invalidParts.length > 0) {
		throw new Error(`Invalid parts detected: parts must have partNumber and eTag properties`);
	}

	// Ensure parts are sorted by part number
	const sortedParts = [...parts].sort((a, b) => a.partNumber - b.partNumber);
	
	console.log(`🚀 Attempting to complete upload with ${MAX_RETRIES} max retries:`, {
		key,
		uploadId,
		partsCount: sortedParts.length,
		partNumbers: sortedParts.map(p => p.partNumber)
	});
	
	for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
		try {
			const config = {
				timeout: 45000, // Increased timeout for completion
			};

			const payload = {
				key,
				uploadId,
				parts: sortedParts,
			};

			console.log(`Complete upload attempt ${attempt}/${MAX_RETRIES} with payload:`, payload);

			const response = await http.post<any>(
				'file-manager',
				'uploads/complete',
				payload,
				config
			);

			console.log(`✅ Upload completed successfully on attempt ${attempt}/${MAX_RETRIES}`);
			return response.data;
		} catch (error: any) {
			const isLastAttempt = attempt === MAX_RETRIES;
			const errorInfo = {
				attempt,
				maxRetries: MAX_RETRIES,
				status: error.response?.status,
				statusText: error.response?.statusText,
				errorMessage: error.message,
				responseData: error.response?.data,
				key,
				uploadId,
				partsCount: sortedParts.length
			};
			
			console.error(`Error in completeUpload API call:`, errorInfo);

			// Check for specific error conditions 
			const is404Error = error.response?.status === 404;
			const isNoSuchUpload = error.message?.includes('NoSuchUpload') || 
							   error.response?.data?.message?.includes('NoSuchUpload');
			
			// For NoSuchUpload on first attempt, it might be a timing issue - retry with longer delay
			if ((is404Error || isNoSuchUpload) && attempt === 1) {
				console.warn(`⚠️  First completion attempt failed with NoSuchUpload - likely timing issue, will retry with longer delay:`, errorInfo);
			} else if ((is404Error || isNoSuchUpload) && attempt > 1) {
				// If it's still failing after retries, the session is truly invalid
				console.error(`❌ Upload session invalid after ${attempt} attempts (NoSuchUpload):`, errorInfo);
				throw new Error(`Upload session expired or invalid after ${attempt} attempts: ${error.message || 'NoSuchUpload'}`);
			}

			// If this was the last attempt, throw the error
			if (isLastAttempt) {
				console.error(`All retry attempts exhausted:`, errorInfo);
				throw error;
			}

			// Wait before retrying (with special handling for timing issues)
			let delayMs: number;
			if ((is404Error || isNoSuchUpload) && attempt === 1) {
				// First NoSuchUpload is likely a timing issue - wait longer
				delayMs = 5000; // 5 seconds for timing issues
			} else {
				// Standard exponential backoff for other errors
				delayMs = RETRY_DELAY * Math.pow(1.5, attempt - 1);
			}
			
			console.log(`⏳ Waiting ${delayMs}ms before retry attempt ${attempt + 1}/${MAX_RETRIES} (reason: ${is404Error || isNoSuchUpload ? 'timing/sync issue' : 'general error'})`);
			await new Promise((resolve) => setTimeout(resolve, delayMs));
		}
	}

	throw new Error(`Failed to complete upload after ${MAX_RETRIES} attempts`);
}
